/**
 * Pipeline Reconcile Page — /sources/reconcile
 *
 * SYSTEM_ADMIN only. Server-side enforced via requireRole on every
 * /api/pipeline-reconcile* endpoint — this client guard is a UX convenience,
 * not the security boundary.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useUser } from '@/hooks/useUser';
import { usePageTitle } from '@/hooks/usePageTitle';
import { PipelineReconcileDashboard } from '@/components/admin/PipelineReconcileDashboard';

export const PipelineReconcilePage: React.FC = () => {
  usePageTitle('Pipeline Reconcile');
  const { user, loading } = useUser();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'SYSTEM_ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center gap-2">
        <RefreshCw className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Pipeline Reconcile
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Finds artifacts stuck at each pipeline stage and stalled/failed
            worker jobs, and requeues them. Requeue-only — never deletes or
            overwrites existing data.
          </p>
        </div>
      </div>

      <PipelineReconcileDashboard />
    </div>
  );
};
